import type { AxiosInstance, InternalAxiosRequestConfig } from 'axios'
import { ElMessage } from 'element-plus'

import { serverErrorMessage } from './serverError'
import {
  RATE_LIMITED_CODE,
  RATE_LIMIT_STATUS,
  noteRateLimited,
  rateLimitMessage,
  rateLimitedError,
  rateLimitedFor
} from './rateLimit'

/**
 * The pages a 401 is the normal answer on — logging in has not happened yet there.
 *
 * Spelled out again rather than shared: the legacy axios instance (`src/utils/request.ts`)
 * applies the same rule and is compiled into both apps, so `src/shared` cannot import it
 * without reaching into the legacy tree. Change one and change the other.
 */
const AUTH_ROUTES = [
  'login',
  'registration',
  'restore',
  'confirmation',
  'reset_password',
  'bcm'
]

function onAuthPage() {
  const { pathname, hash } = window.location

  return (
    AUTH_ROUTES.some(
      (route) => pathname.includes(route) || hash.includes(route)
    ) ||
    (pathname === '/' && !hash) ||
    hash === '#/'
  )
}

/**
 * A refusal for asking too often, told once and then obeyed.
 *
 * Told, because nothing else does: the toast below is for 5xx only, so an unannounced 429
 * reaches the screen as an empty table with no reason under it. Obeyed, because the request
 * interceptor stops sending until the window closes — the answer to "you are asking too
 * often" must not be another question.
 */
function handleRateLimited(error: unknown, cfg?: InternalAxiosRequestConfig) {
  const opensWindow = noteRateLimited(error)

  if (!opensWindow || cfg?.skipGlobalErrorHandler === true) return

  ElMessage({
    type: 'warning',
    message: rateLimitMessage(),
    duration: 8000,
    showClose: true
  })
}

function showGlobalServerErrorToast(error: unknown) {
  ElMessage({
    type: 'error',
    message: serverErrorMessage(
      error,
      'A server error occurred. Please try again later.'
    ),
    duration: 5000,
    showClose: true
  })
}

/**
 * What every request and every failed answer goes through — called once, by `http.ts`.
 *
 * adok authenticates by **session cookie**, so the instance sends `withCredentials: true`
 * and no `Authorization` header. Nothing here reads a token out of `localStorage`, and
 * nothing should: a token every script on the origin can read is worse than the cookie.
 */
export function setupInterceptors(http: AxiosInstance) {
  /**
   * While the cooldown is open, a request is refused here rather than sent.
   *
   * Nothing else can do this: every widget on a page asks on its own, TanStack refetches on
   * focus, tables offer "Try again" and the job poller asks every two seconds, so one 429
   * without this becomes a dozen more — each of them another entry against the very budget
   * the user is waiting to get back.
   *
   * Refused, not queued. A queue releases the whole burst the instant the window closes,
   * which is the same burst one moment later, and a caller holding a request cannot tell
   * the user anything about it.
   */
  http.interceptors.request.use((config) => {
    if (rateLimitedFor() === 0) return config

    return Promise.reject(rateLimitedError(config))
  })

  http.interceptors.response.use(
    (res) => res,

    (error) => {
      const status = error.response?.status
      const cfg = error.config as InternalAxiosRequestConfig | undefined

      // Held back by the interceptor above, which means the user was told when the window
      // opened. Saying it again once per widget is what the window exists to prevent.
      if (error.code === RATE_LIMITED_CODE) return Promise.reject(error)

      if (status === RATE_LIMIT_STATUS) {
        handleRateLimited(error, cfg)
        return Promise.reject(error)
      }

      // One contract for an expired session, on both axios instances: it is over, go and
      // sign in. Anything quieter leaves half the app showing empty tables instead.
      if (status === 401 && !onAuthPage()) {
        window.location.href = '/'
      }

      if (
        cfg?.skipGlobalErrorHandler !== true &&
        status != null &&
        status >= 500
      ) {
        showGlobalServerErrorToast(error)
      }

      return Promise.reject(error)
    }
  )
}
